import { overlayToSvg, type Overlay } from '../../domain/annotation';
import { oeffneErneut } from '../../persistence/anhaenge';
import { getStore } from '../../persistence/store';
import { deleteFileMitSignatur, overlayPfad, saveTextAs } from '../../platform/storage';
import { currentFileSubject } from '../../store/useAppStore';

/**
 * Sichert die Markierungsebene eines Fotos — herausgelöst aus der Grossansicht, damit sich der
 * Entscheid „schreiben, überschreiben, löschen oder nichts tun" ohne React prüfen lässt
 * (dasselbe Vorgehen wie bei `handwritingSave.ts`; `vitest.config.ts` läuft mit
 * `environment: 'node'`).
 */

/**
 * Inhaltlicher Vergleich zweier Ebenen — keine Objektgleichheit: die Bearbeitung legt bei jeder
 * Änderung neue Objekte an, ein Referenzvergleich hielte deshalb jedes blosse Ansehen für eine
 * Änderung. `JSON.stringify` genügt, weil `Overlay` nur Zahlen und Text enthält — dieselbe
 * Technik wie `strokesGleich` in `handwritingSave.ts`.
 *
 * `null` (es gab keine Datei) und eine leere Ebene (es gab eine, sie wurde geräumt) sind
 * dabei ausdrücklich **nicht** dasselbe: nur der zweite Fall löst ein Löschen aus.
 */
export function overlayGleich(a: Overlay | null, b: Overlay | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * `vorher` ist der Stand, mit dem der Prüfer die Ebene vorgefunden hat (`null`, wenn es keine
 * Datei gab), `nachher` der beim Schliessen.
 *
 * Drei Ausgänge:
 * - **unverändert:** nichts geschieht. Ohne diese Wache legte jedes blosse Öffnen und
 *   Schliessen eine neue Datei an.
 * - **leer geräumt:** Datei **und** Beglaubigung werden gelöscht.  Eine leere Ebene zu
 *   schreiben hiesse, eine Datei ohne Inhalt mit in die Übertragung zu geben; sie verdeckte
 *   nichts, wäre aber da.
 * - **geändert:** dieselbe Datei wird überschrieben und neu beglaubigt.
 *
 * ⚠️ **Eine Protokollzeile entsteht in keinem der drei Fälle** (seit 2026-09-14). Der `value`
 * der Medienzeilen trägt jetzt die **Anzahl** der Aufnahmen (`domain/medienzahl.ts`), und die
 * Ebene gehört zu ihrem Foto: sie zählt nicht extra (Entscheidung des Nutzers). Ihre Zeile
 * trüge also nur die unveränderte Fotoanzahl — und verdrängte dabei den jüngsten Eintrag, den
 * der Server als einzigen auswertet.
 *
 * Die **Datei** geht weiterhin hinaus: `server/anhaenge.ts` führt die Ebene als eigene Art
 * `overlay` und findet sie über das Dateisystem, nicht über das Protokoll.
 */
export async function sichereOverlay(
  fotoPfad: string,
  taskId: string,
  vorher: Overlay | null,
  nachher: Overlay,
): Promise<void> {
  if (overlayGleich(vorher, nachher)) return;

  const ziel = overlayPfad(fotoPfad);

  if (nachher.markers.length === 0) {
    // Gab es vorher gar keine Datei, ist auch nichts zu löschen — `deleteFileMitSignatur`
    // schluckt „nicht gefunden", der Aufruf bleibt also folgenlos, aber unnötig.
    if (vorher !== null) {
      await deleteFileMitSignatur(ziel);
      // Die Zeile über die Übertragung zeigte sonst auf eine Datei, die es nicht mehr gibt.
      await oeffneErneut(getStore(), ziel);
    }
    return;
  }

  const subject = { ...currentFileSubject(), taskId };
  // Im SVG steht der blosse Dateiname des Bildes, nicht sein Ordnerpfad: die Ebene liegt
  // ohnehin daneben, und ein Pfad würde beim Verschieben des Ordners falsch.
  const bildDatei = fotoPfad.slice(fotoPfad.lastIndexOf('/') + 1);
  await saveTextAs(ziel, overlayToSvg(nachher, bildDatei), subject);

  /*
   * ⚠️ **Die Ebene behält ihren Namen, also auch ihren Übertragungsstand.** War sie im
   * Relay-Modus schon hinausgegangen, stünde `erledigt='Y'` daneben und die geänderte Fassung
   * ginge nie wieder hinaus — am Gerät die neue Markierung, in der Auswertung die alte
   * (`persistence/anhaenge.ts::oeffneErneut`).
   */
  await oeffneErneut(getStore(), ziel);
}
